import { Tag } from 'antd';
import { MobileOutlined, PhoneOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../contexts/AuthContext';
import { PensionerStatus } from '../../utils/constants/tagColors';
import { useErrorMessage } from '../../utils/errorMessage';
import { useListResourceArchiving } from '../../utils/listResourceArchiving';
import { postalCodeFilter } from './utils/pensionersFilters';
import { genericSorter, statusSorter } from '../../utils/sorterMethods';
import { createActionColumn } from '../../utils/createActionColumn';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';
import { PersonCell, TwoLinesCell } from '../../components/ListCells/ListCells';
/**
 * Custom hook that returns columns and headers for the pensioners table.
 * @hook
 * @param {Function} setForceRefresh - Function to force refresh the table.
 * @returns {Object} - Object containing columns and headers.
 */
export const useColumns = (setForceRefresh) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const { deleteResource, restoreResource } = useListResourceArchiving(
    setForceRefresh,
    'pensioners/archiving'
  );

  const setColumnHeaderStyle = () => ({
    onHeaderCell: () => ({
      style: {
        whiteSpace: 'nowrap'
      }
    })
  });

  const [enums, setEnums] = useState();
  const searchInput = useRef(null);

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/pensioners/enums' });
      setEnums(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      await getEnums();
    })();
  }, []);

  const columns = [
    {
      title: t('pensioners.form.status'),
      key: 'status',
      dataIndex: ['status'],
      render: (status) =>
        status ? (
          <Tag color={PensionerStatus[status]}>
            {t(`pensioners.tags.${status}`, {
              defaultValue: status
            })}
          </Tag>
        ) : (
          'N/R'
        ),
      filters: enums?.status?.map((r) => ({
        text: t(`pensioners.tags.${r}`),
        value: r
      })),
      sorter: (a, b) => statusSorter(a, b, 'pensioners.tags', t)
    },
    {
      title: t('pensioners.form.reference'),
      key: 'reference',
      dataIndex: 'reference',
      width: '5%',
      sorter: true,
      ...setColumnHeaderStyle()
    },
    {
      title: t('pensioners.form.principal_pensioner'),
      key: 'last_name',
      render: (record) => {
        const civility = record?.civility
          ? t(`pensioners.tags.${record?.civility}`)
          : '';
        const lastName = record?.last_name || '';
        const firstName = record?.first_name || '';

        return (
          <PersonCell
            firstName={firstName}
            lastName={lastName}
            name={`${civility} ${lastName} ${firstName}`}
            subtitle={record?.email}
          />
        );
      },
      sorter: (a, b) => genericSorter(a, b, ['last_name'])
    },
    {
      title: t('pensioners.form.secondary_pensioner'),
      key: 'secondary_profile.last_name',
      render: (record) => {
        const civility = record?.secondary_profile?.civility
          ? t(`pensioners.tags.${record?.secondary_profile?.civility}`)
          : '';
        const lastName = record?.secondary_profile?.last_name || '';
        const firstName = record?.secondary_profile?.first_name || '';

        return (
          <TwoLinesCell
            main={`${civility} ${lastName} ${firstName}`.trim()}
            sub={formatPhoneNumber(
              record?.secondary_profile?.cell_phone_number
            )}
          />
        );
      },
      sorter: (a, b) => genericSorter(a, b, ['secondary_profile', 'last_name'])
    },
    {
      title: t('pensioners.form.address.city'),
      key: 'address.postal_code',
      dataIndex: 'address',
      ...postalCodeFilter(searchInput, t),
      render: (address) => (
        <TwoLinesCell
          main={address?.city}
          sub={[address?.postal_code, address?.state]
            .filter(Boolean)
            .join(' · ')}
        />
      ),
      sorter: true,
      ...setColumnHeaderStyle()
    },
    {
      title: t('pensioners.form.phone_number_full'),
      key: 'cell_phone_number',
      render: (record) => (
        <div className="cell-lines">
          <span className="cell-sub">
            <MobileOutlined />{' '}
            {formatPhoneNumber(record?.cell_phone_number) || '-'}
          </span>
          <span className="cell-sub">
            <PhoneOutlined /> {formatPhoneNumber(record?.phone_number) || '-'}
          </span>
        </div>
      ),
      sorter: true,
      ...setColumnHeaderStyle()
    },
    createActionColumn({
      t,
      deleteResource,
      restoreResource,
      resourcePath: 'pensioners'
    })
  ];

  return { columns };
};
